/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { api, PURE_SOULS_URL, SITE_URL } from "@/lib/site";
import ReadingProgress from "@/components/site/ReadingProgress";
import ShareBar from "@/components/site/ShareBar";
import { LeafDivider } from "@/components/site/Motifs";
import Seo from "@/components/site/Seo";
import { breadcrumbLd, articleLd } from "@/lib/seo";

function slugifyHeading(text, i) {
  const base = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
  return `s-${i}-${base}`;
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState("loading");
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    setStatus("loading");
    window.scrollTo(0, 0);
    api
      .get(`/articles/${slug}`)
      .then((res) => {
        setArticle(res.data);
        setStatus("ok");
      })
      .catch(() => setStatus("error"));

    api
      .get("/articles", { params: { limit: 100 } })
      .then((res) => {
        const others = res.data.filter((a) => a.slug !== slug && !a.is_stub);
        setRelated(others.slice(0, 3));
      })
      .catch(() => setRelated([]));
  }, [slug]);

  // Inject ids into <h2> headings and build a table of contents.
  const { html, toc } = useMemo(() => {
    if (!article?.body) return { html: "", toc: [] };
    const doc = new DOMParser().parseFromString(article.body, "text/html");
    const items = [];
    doc.querySelectorAll("h2").forEach((h, i) => {
      const id = slugifyHeading(h.textContent, i);
      h.setAttribute("id", id);
      items.push({ id, text: h.textContent });
    });
    return { html: doc.body.innerHTML, toc: items };
  }, [article]);

  // Scroll-spy: highlight the heading nearest the top.
  useEffect(() => {
    if (toc.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    toc.forEach((t) => {
      const el = document.getElementById(t.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [toc, html]);

  if (status === "loading") {
    return (
      <div className="container-editorial section-padding">
        <p className="text-forest-700">Loading…</p>
      </div>
    );
  }
  if (status === "error" || !article) {
    return (
      <div className="container-editorial section-padding" data-testid="article-not-found">
        <Seo
          title="Article not found | Pet Water Cremation Australia"
          description="The article you are looking for may have been moved or is still being written."
          path="/articles"
          noindex
        />
        <h1 className="font-serif text-4xl text-forest-900 mb-4">Article not found</h1>
        <p className="text-forest-700 mb-6">
          The article you are looking for may have been moved or is still being written.
        </p>
        <Link to="/articles" className="btn-secondary">Back to Learning Centre</Link>
      </div>
    );
  }

  if (article.is_stub) {
    return (
      <div className="container-editorial section-padding" data-testid="article-stub-page">
        <Seo
          title={`${article.title} | Pet Water Cremation Australia`}
          description={article.excerpt}
          path={`/articles/${article.slug}`}
          image={article.hero_image}
          noindex
        />
        <Link to="/articles" className="link-underline text-sm">← Back to Learning Centre</Link>
        <div className="overline mt-8 mb-4">{article.category}</div>
        <h1 className="font-serif text-5xl sm:text-6xl text-forest-900 tracking-tight leading-tight max-w-4xl">
          {article.title}
        </h1>
        <p className="text-xl text-forest-700 mt-6 max-w-2xl leading-[1.7]">{article.excerpt}</p>
        <div className="mt-10 bg-sand-100 border border-sand-300 rounded-2xl p-8 max-w-2xl">
          <div className="overline mb-2">Coming Soon</div>
          <p className="text-forest-700 leading-relaxed">
            This article is currently being written as part of our growing educational
            library. While you wait, you can explore the foundational articles already
            published in our Learning Centre.
          </p>
          <Link to="/articles" className="btn-secondary mt-6 inline-flex">Browse published articles</Link>
        </div>
      </div>
    );
  }

  return (
    <article data-testid="article-detail-page">
      <Seo
        title={`${article.title} | Pet Water Cremation Australia`}
        description={article.excerpt}
        path={`/articles/${article.slug}`}
        image={article.hero_image}
        type="article"
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Learning Centre", path: "/articles" },
            { name: article.title, path: `/articles/${article.slug}` },
          ]),
          articleLd(article),
        ]}
      />
      <ReadingProgress />

      {/* Hero */}
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={article.hero_image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#F9F9F6]/82" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F9F9F6]/40 via-transparent to-[#F9F9F6]" />
        </div>
        <div className="aura bg-sage-300/28 w-[40vw] h-[40vw] -top-[12vw] -left-[10vw]" />
        <div className="aura bg-water-300/36 w-[32vw] h-[32vw] top-[10%] -right-[8vw]" />
        <div className="container-editorial pt-16 pb-12">
          <Link to="/articles" className="link-underline text-sm" data-testid="article-back-link">
            ← Back to Learning Centre
          </Link>
          <div className="overline mt-8 mb-4">{article.category} · {article.read_time}</div>
          <h1
            className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-forest-900 max-w-4xl"
            data-testid="article-title"
          >
            {article.title}
          </h1>
          <p className="mt-6 max-w-2xl text-lg sm:text-xl text-forest-700 leading-[1.65]">
            {article.excerpt}
          </p>
          <div className="mt-8">
            <ShareBar
              title={article.title}
              url={`${SITE_URL}/articles/${article.slug}`}
            />
          </div>
        </div>
      </section>

      {/* Hero image full-width */}
      <div className="container-editorial mb-16">
        <div className="relative">
          <div className="absolute -inset-4 -z-10 rounded-[2.5rem] bg-gradient-to-tr from-sage-300/30 via-water-300/42 to-clay-200/22 blur-3xl opacity-70" />
          <div className="glass-strong rounded-[1.9rem] p-3">
            <div className="aspect-[16/9] sm:aspect-[16/7] rounded-[1.4rem] overflow-hidden">
              <img src={article.hero_image} alt={article.title} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>

      {/* Body + sticky table of contents */}
      <section className="container-editorial pb-20">
        {toc.length > 1 && (
          <details className="lg:hidden mb-8 card-elevated rounded-2xl px-5 py-4" data-testid="article-toc-mobile">
            <summary className="flex items-center justify-between cursor-pointer list-none">
              <span className="overline">On this page</span>
              <ChevronDown size={18} className="toc-chevron text-forest-500" />
            </summary>
            <nav className="mt-4 space-y-3 border-t border-sand-300 pt-4">
              {toc.map((t) => (
                <a key={t.id} href={`#${t.id}`} className="block text-sm text-forest-600 py-1">
                  {t.text}
                </a>
              ))}
            </nav>
          </details>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {toc.length > 1 && (
            <aside className="hidden lg:block lg:col-span-3" data-testid="article-toc">
              <div className="sticky top-28">
                <div className="overline mb-4">On this page</div>
                <nav className="space-y-3 border-l border-sand-300 pl-4">
                  {toc.map((t) => (
                    <a key={t.id} href={`#${t.id}`} className="toc-link" data-active={activeId === t.id}>
                      {t.text}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>
          )}

          <div className={toc.length > 1 ? "lg:col-span-9 lg:max-w-2xl" : "max-w-2xl mx-auto"}>
            <div
              className="editorial-prose article-dropcap"
              data-testid="article-body"
              dangerouslySetInnerHTML={{ __html: html }}
            />

            <div className="mt-12 pt-8 border-t border-sand-300">
              <ShareBar
                title={article.title}
                url={`${SITE_URL}/articles/${article.slug}`}
                label="Found this helpful? Share it"
                testIdPrefix="share-bottom"
              />
            </div>

            <LeafDivider className="my-12" label="Featured Provider" />

            <div className="glass-strong rounded-2xl p-8">
              <div className="overline mb-3">Premium Provider, Queensland</div>
              <h3 className="font-serif text-2xl text-forest-900 leading-tight mb-2">
                Pure Souls Pet Aqua Cremation
              </h3>
              <p className="text-forest-700 leading-relaxed mb-5">
                Families in Queensland seeking premium water cremation services can learn
                more through Pure Souls Pet Aqua Cremation in Townsville.
              </p>
              <a
                href={PURE_SOULS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="article-pure-souls-cta"
              >
                Visit Pure Souls →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-sand-100 section-padding">
          <div className="container-editorial">
            <div className="overline mb-3">Continue Reading</div>
            <h2 className="font-serif text-4xl text-forest-900 tracking-tight mb-12">
              Related articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to={`/articles/${a.slug}`}
                  className="group block"
                  data-testid={`related-article-${a.slug}`}
                >
                  <div className="aspect-[5/4] rounded-2xl overflow-hidden bg-sand-200 mb-5">
                    <img
                      src={a.hero_image}
                      alt={a.title}
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="overline mb-2">{a.category}</div>
                  <h3 className="font-serif text-2xl text-forest-900 leading-tight tracking-tight mb-2">
                    {a.title}
                  </h3>
                  <p className="text-forest-700 text-[15px] leading-relaxed">{a.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
